/** 屏幕坐标 ↔ 毫米坐标换算，以及剪口命中测试。 */
import type { CutShape, Polygon, Vec } from '../domain/types';
import { cutToPolygon } from '../domain/folds/geometry';
import { boundsOf, pointInPolygon, distance, type Bounds } from '../domain/geometry/polygon';

export interface Viewport {
  scale: number;
  tx: number;
  ty: number;
}

export const IDENTITY_VIEW: Viewport = { scale: 1, tx: 0, ty: 0 };

/** 用 SVG 自身矩阵把客户端坐标转成毫米坐标（自动兼容缩放与平移）。 */
export function clientToWorld(el: SVGGraphicsElement | null, clientX: number, clientY: number): Vec | null {
  if (!el) return null;
  const ctm = el.getScreenCTM();
  if (!ctm) return null;
  const pt = el.ownerSVGElement?.createSVGPoint();
  if (!pt) return null;
  pt.x = clientX;
  pt.y = clientY;
  const local = pt.matrixTransform(ctm.inverse());
  return { x: local.x, y: local.y };
}

/** 带留白的 viewBox，保持毫米单位。 */
export function viewBoxFromBounds(b: Bounds, padMm = 12): string {
  const w = Math.max(b.maxX - b.minX, 10) + padMm * 2;
  const h = Math.max(b.maxY - b.minY, 10) + padMm * 2;
  return `${b.minX - padMm} ${b.minY - padMm} ${w} ${h}`;
}

export function boundsOfPolygon(poly: Polygon): Bounds {
  return boundsOf(poly);
}

export function cutBounds(cut: CutShape): Bounds {
  return boundsOf(cutToPolygon(cut));
}

/** 命中测试：返回最上层被点中的剪口 id。 */
export function hitTest(cuts: CutShape[], p: Vec): string | null {
  for (let i = cuts.length - 1; i >= 0; i--) {
    const cut = cuts[i];
    if (cut.kind === 'circle') {
      if (distance(p, cut.center) <= cut.radius) return cut.id;
      continue;
    }
    const poly = cutToPolygon(cut);
    if (pointInPolygon(p, poly)) return cut.id;
  }
  return null;
}

/** 几何剪口在中心到半径方向给出的缩放手柄位置。 */
export function cutHandle(cut: CutShape): Vec | null {
  switch (cut.kind) {
    case 'circle':
      return { x: cut.center.x + cut.radius, y: cut.center.y };
    case 'triangle':
      return { x: cut.center.x + cut.radius, y: cut.center.y };
    case 'rect':
      return { x: cut.center.x + cut.width / 2, y: cut.center.y + cut.height / 2 };
    default:
      return null;
  }
}

export function isGeometric(cut: CutShape): boolean {
  return cut.kind === 'circle' || cut.kind === 'triangle' || cut.kind === 'rect';
}

/** 按手柄拖动量缩放几何剪口。 */
export function scaleCut(cut: CutShape, handleWorld: Vec, startWorld: Vec): CutShape {
  switch (cut.kind) {
    case 'circle': {
      const r = Math.max(0.6, distance(cut.center, handleWorld));
      return { ...cut, radius: r };
    }
    case 'triangle': {
      const r = Math.max(0.8, distance(cut.center, handleWorld));
      return { ...cut, radius: r };
    }
    case 'rect': {
      const startDist = Math.max(0.2, Math.abs(startWorld.x - cut.center.x) * 2);
      const currentDist = Math.max(0.6, Math.abs(handleWorld.x - cut.center.x) * 2);
      const ratio = currentDist / startDist;
      return {
        ...cut,
        width: Math.max(0.8, cut.width * ratio),
        height: Math.max(0.8, cut.height * ratio),
      };
    }
    default:
      return cut;
  }
}